/**
 * DocumentPreviewDialog - Modal dialog for previewing extracted CD data
 *
 * Displays the extracted data from a processed document rendered
 * on the Closing Disclosure template.
 */

import { formatDistanceToNow } from 'date-fns';
import { AlertCircle, FileText, Loader2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { closingDisclosureApi } from '../../services';
import type { ClosingDisclosureData } from '../../types/closingDisclosureData';
import { DEFAULT_CLOSING_DISCLOSURE_DATA } from '../../types/closingDisclosureData';
import { ClosingDisclosure } from '../ClosingDisclosure';
import { PrintButton } from '../PrintPreview';

// Import print styles
import '../../styles/print.css';

export interface DocumentPreviewDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when dialog should close */
  onOpenChange: (open: boolean) => void;
  /** Transaction ID */
  transactionId: string;
  /** Document ID to preview */
  documentId: string | null;
}

/**
 * Transform extraction data to ClosingDisclosureData format
 * This is a simplified transform - the actual structure depends on
 * what the PDF extraction service returns
 */
function transformExtractionDataToCD(extractionData: any): ClosingDisclosureData {
  // For now, return a basic structure with the extracted data
  // This will need to be adjusted based on actual extraction format
  return {
    ...DEFAULT_CLOSING_DISCLOSURE_DATA,
    // Merge extraction data into the structure
    ...(extractionData?.page1 && { page1: extractionData.page1 }),
    ...(extractionData?.page2 && { page2: extractionData.page2 }),
    ...(extractionData?.page3 && { page3: extractionData.page3 }),
    ...(extractionData?.page4 && { page4: extractionData.page4 }),
    ...(extractionData?.page5 && { page5: extractionData.page5 }),
  };
}

/**
 * Safely format a date string to relative time
 * Handles invalid dates gracefully
 */
const formatRelativeTime = (dateString: string | Date | undefined): string => {
  if (!dateString) return 'Unknown';
  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    // Validate the date
    if (isNaN(date.getTime())) {
      return 'Unknown';
    }
    return formatDistanceToNow(date, { addSuffix: true });
  } catch {
    return 'Unknown';
  }
};

export function DocumentPreviewDialog({
  open,
  onOpenChange,
  transactionId,
  documentId,
}: DocumentPreviewDialogProps) {
  const [cdData, setCdData] = useState<ClosingDisclosureData>(DEFAULT_CLOSING_DISCLOSURE_DATA);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [documentName, setDocumentName] = useState<string>('');
  const [processedAt, setProcessedAt] = useState<string>('');

  useEffect(() => {
    if (!open || !documentId) return;

    const loadDocument = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await closingDisclosureApi.getDocument(transactionId, documentId);

        if (response.success && response.data) {
          setDocumentName(response.data.fileName || 'Document');
          setProcessedAt(response.data.updatedAt || response.data.uploadedAt);

          if (response.data.processingStatus === 'completed' && response.data.extractionData) {
            const transformed = transformExtractionDataToCD(response.data.extractionData);
            setCdData(transformed);
          } else if (response.data.processingStatus === 'failed') {
            setError(response.data.errorMessage || 'Document processing failed');
          } else {
            setError('Document processing has not completed yet');
          }
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load document');
      } finally {
        setIsLoading(false);
      }
    };

    loadDocument();
  }, [open, documentId, transactionId]);

  const handleClose = () => {
    onOpenChange(false);
    // Reset state after animation
    setTimeout(() => {
      setCdData(DEFAULT_CLOSING_DISCLOSURE_DATA);
      setError(null);
      setDocumentName('');
      setProcessedAt('');
    }, 200);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-muted-foreground" />
              <div>
                <DialogTitle className="text-lg">{documentName || 'Document Preview'}</DialogTitle>
                {processedAt && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Processed {formatRelativeTime(processedAt)}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!isLoading && !error && <PrintButton documentTitle={documentName} />}
              <Button variant="ghost" size="icon" onClick={handleClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(90vh-120px)]">
          <div className="p-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="text-center">
                  <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">Loading document data...</p>
                </div>
              </div>
            ) : error ? (
              <Alert variant="destructive" className="max-w-md mx-auto mt-8">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : (
              <div className="bg-white shadow-lg print:shadow-none">
                <ClosingDisclosure
                  initialData={cdData}
                  editable={false}
                  showPageSeparators={true}
                />
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
